import React, { useState, useEffect } from 'react';
import { usePracticeFieldsState, usePracticeFieldsDispatch } from '../../../store/hooks/practiceFields';
import { usePrevious } from '../../../helpers/customHooks';
import { Row, Col, Button, Form } from 'react-bootstrap';

import MapForm from './PracticeAreaUtils/MapForm';
import PracticeTable from './PracticeAreaUtils/PracticeTable';
import PracticeAreaMap from './Map/PracticeAreaMap';

import { ADD_ACTION, EDIT_ACTION } from '../../../config/constants';
import { getPracticeAreaHeader, isBlankString, uploadFileImage, isEmptyObject, getObjectLength } from '../../../helpers/helpers';
import { message } from 'antd';
import { useParams } from 'react-router-dom';
import { PracticeAreas, PracticeFieldsDispatch, PracticeFieldsState, FormField } from './types';

export default ({ field, setField, formState, imageElement, setFormState } : PracticeAreas) => {

  const { fields: practiceFields = {}, actions: showArea = {} } : PracticeFieldsState = usePracticeFieldsState();
  const { 
    getAreaFieldImage, 
    editPracticeField, 
    addNewPracticeField, 
    removePracticeField, 
    setPracticeFieldActions 
  } : PracticeFieldsDispatch = usePracticeFieldsDispatch();

  const { teamId } = useParams<{ teamId: string }>();
  const prevAreaCoords : number[] | undefined = usePrevious(formState.areaCoords);
  const [imageDimensions, setImageDimensions] = useState<number[]>([]);

  // set field on OL after load
  useEffect(() => {
    const { editingIndex } = showArea;
    if (editingIndex && practiceFields[editingIndex]) {
      if (practiceFields[editingIndex].image) {
        const { image, _id } = practiceFields[editingIndex];
        setField({ _id, image });
      } else {
        const { _id } = practiceFields[editingIndex];
        getAreaFieldImage(teamId, _id, editingIndex);
      }
    }
  }, [practiceFields]);

  // if it's a new field, adjust the area width/height on map coords
  useEffect(() => {
    const { areaCoords, areaFeetDimensions } = formState;
    if (areaCoords && prevAreaCoords) {
      const [x0, y0, x1, y1] = areaCoords;
      const [prevX0, prevY0, prevX1, prevY1] = prevAreaCoords as number[];
      const [areaWidth, areaHeight] = areaFeetDimensions;

      if ((prevX1 - prevX0) === areaWidth && (prevY1 - prevY0) === areaHeight) {
        setFormState({ ...formState, areaFeetDimensions: [x1 - x0, y1 - y0] });
      }
    }
  }, [formState.areaCoords]);

  const handleEditClick = (key: string) => {
    const { _id, image, x0, y0, x1, y1, lat, lng, widthFt, heightFt, ...rest } = practiceFields[key];
    setFormState({
      ...rest,
      areaCoords: [x0, y0, x1, y1],
      areaFeetDimensions: [widthFt, heightFt]
    });
    setStateIfLoaded(image, _id);
    setPracticeFieldActions({ action: EDIT_ACTION, editingIndex: key });
  };

  const setStateIfLoaded = (imageURL: string, _id: string) => {
    if (!imageURL) {
      getAreaFieldImage(teamId, _id);
    } else {
      setField({ _id, image: imageURL });
    }
  };

  const handleDeleteClick = (key: string) => {
    const { _id } = practiceFields[key];
    removePracticeField(teamId, _id);
    handleCancelClick();
  };

  const handleImageDimensions = (image: HTMLImageElement) => {
    const { editingIndex, action } = showArea;
    const { naturalWidth, naturalHeight } = image;
    setImageDimensions([naturalWidth, naturalHeight]);

    if (
      action === ADD_ACTION || (editingIndex && practiceFields[editingIndex] && 
      practiceFields[editingIndex].image && practiceFields[editingIndex].image !== field.image)
    ) {
      setFormState({ 
        ...formState, 
        areaCoords: [0, 0, naturalWidth, naturalHeight],
        areaFeetDimensions: [naturalWidth, naturalHeight] 
      });
    } else if (editingIndex&& practiceFields[editingIndex]) {
      setFormState({
        ...formState,
        areaCoords: [
          practiceFields[editingIndex].x0,
          practiceFields[editingIndex].y0,
          practiceFields[editingIndex].x1,
          practiceFields[editingIndex].y1,
        ]
      });
    }
  };

  const saveImageToState = (imageFile: File) => {
    setFormState({ ...formState, fileName: imageFile.name });
    setField({
      _id: imageFile.name,
      image: URL.createObjectURL(imageFile),
      file: imageFile
    });
  };

  const handleOnSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const { action, editingIndex } = showArea;
    const { areaCoords, areaFeetDimensions, fileName, ...rest } = formState;
    const [x0, y0, x1, y1] = areaCoords;
    const [widthFt, heightFt] = areaFeetDimensions;

    const newField = { ...rest, x0, y0, x1, y1, widthFt, heightFt };
    const imageTo64 = await uploadFileImage(field.file as File); // uploading file, if not exists, nothing happens
    
    if (action === ADD_ACTION) {
      const duplicateName = Object.entries(practiceFields).some(([key, field]) => {
        return field.areaName === formState.areaName;
      });

      if (!duplicateName) {
        addNewPracticeField(teamId, newField, imageTo64);
      } else {
        message.error({
          content: 'There is already a field with that name.',
          className: 'antd-message-alert'
        });
      }
    } else if (editingIndex) {
      const editFormat : FormField = { ...newField, id: editingIndex };
      if (formState.areaName === practiceFields[editingIndex].areaName) {
        delete editFormat.areaName;
      } 
      editPracticeField(teamId, editFormat, imageTo64);
    }
  };

  const handleCancelClick = () => {
    setPracticeFieldActions({});
  };

  const { editingIndex } = showArea; 
  const { areaName, areaCoords } = formState;
  const [x0, y0, x1, y1] = areaCoords;

  return (
    <div className="pa-body">
      {!isEmptyObject(practiceFields) ? (
        <div className="pa-table-content">
          <PracticeTable
            editingIndex={editingIndex}
            practiceFields={practiceFields}
            handleEditClick={handleEditClick}
            handleDeleteClick={handleDeleteClick}
          />
        </div>
      ) : <></>}

      {showArea.action && (
        <div className="pa-map-content" style={getObjectLength(practiceFields) >= 3 || getObjectLength(practiceFields) === 0 ? { marginTop: 30 } :
        getObjectLength(practiceFields) === 2 ? { marginTop: -15 } : {marginTop: '-7%'}}>
          <div className="title-b">
            {getPracticeAreaHeader(showArea.action, practiceFields[editingIndex || ''])}
          </div>

          <Form onSubmit={handleOnSubmit} autoComplete="off">
            <Row>
              <Col lg={7}>
                <img
                  src={field.image}
                  ref={imageElement}
                  onLoad={() => {
                    handleImageDimensions(imageElement.current as HTMLImageElement);
                  }}
                  className="img-fluid"
                  alt=""
                  id="field-image"
                  style={{ display: 'none' }}
                />
                <PracticeAreaMap
                  field={field}
                  setField={setField}
                  areaCoords={areaCoords}
                  setAreaCoords={(areaCoords: number[]) => setFormState({ ...formState, areaCoords })}
                  selectedField={editingIndex ? practiceFields[editingIndex] : null}
                  imageDimensions={imageDimensions}
                />
              </Col>

              <Col lg={5}>
                <MapForm 
                  formState={formState}
                  imageElement={imageElement}
                  setFormState={setFormState}
                  saveImageToState={saveImageToState}
                />
              </Col>
            </Row>

            <Row style={{ marginTop: '40px', marginBottom: 20 }}>
              <Col lg={12} className="text-center">
                <Button className="btn-transparet" style={{ maxWidth: '67px', height: '31px' }} onClick={handleCancelClick}>Cancel</Button>
                <Button
                  type={'submit'}
                  className="btn-blue"
                  style={areaName ? {} : { opacity: '0.5', height: '31px' }}
                  disabled={isBlankString(areaName) && !isNaN(x0) && !isNaN(y0) && !isNaN(x1) && !isNaN(y1)}
                >{editingIndex ? 'Save' : 'Create'}</Button>
              </Col>
            </Row>
          </Form>
        </div>
      )}
    </div>
  );
};
